import { Check, FileText } from "lucide-react";
import { TagInput, TextField } from "@/components/forms";
import { CertificationEditor } from "./CertificationEditor";
import type { CertificationDraft, CertificationEntry } from "./types";
import type { CertificationField, FieldErrors } from "./validation";

interface SkillsStepProps {
  skills: string[];
  onSkillsChange: (next: string[]) => void;
  skillsError?: string | null;

  certifications: CertificationEntry[];
  certDraft: CertificationDraft;
  onCertDraftChange: (field: keyof CertificationDraft, value: string) => void;
  onAddCertification: () => void;
  onRemoveCertification: (id: string) => void;
  certError?: string | null;
  certErrors?: FieldErrors<CertificationField>;
  certEntryErrors?: Record<string, string>;
  awardedToPlaceholder?: string;
  formatCertDate: (date: string) => string;

  resumeFile: File | null;
  resumeParsed: boolean;
  resumeUrl: string;
  onResumeUrlChange: (value: string) => void;
  resumeUrlError?: string | null;
}

export function SkillsStep({
  skills,
  skillsError,
  onSkillsChange,
  certifications,
  certDraft,
  onCertDraftChange,
  onAddCertification,
  onRemoveCertification,
  certError,
  certErrors,
  certEntryErrors,
  awardedToPlaceholder,
  formatCertDate,
  resumeFile,
  resumeParsed,
  resumeUrl,
  onResumeUrlChange,
  resumeUrlError,
}: SkillsStepProps) {
  return (
    <div className="space-y-7">
      <section className="space-y-2">
        <h3 className="text-sm font-bold" style={{ color: "var(--landing-text)" }}>
          Skills
        </h3>
        <TagInput
          value={skills}
          onChange={onSkillsChange}
          placeholder="Type a skill and press Enter"
          hint="Add the tools, languages, and competencies that match this role."
          error={skillsError}
        />
      </section>

      <section className="space-y-2">
        <h3 className="text-sm font-bold" style={{ color: "var(--landing-text)" }}>
          Certifications
        </h3>
        <CertificationEditor
          certifications={certifications}
          draft={certDraft}
          onDraftChange={onCertDraftChange}
          onAdd={onAddCertification}
          onRemove={onRemoveCertification}
          error={certError}
          errors={certErrors}
          entryErrors={certEntryErrors}
          awardedToPlaceholder={awardedToPlaceholder}
          formatCertDate={formatCertDate}
        />
      </section>

      <section className="space-y-2.5">
        <h3 className="text-sm font-bold" style={{ color: "var(--landing-text)" }}>
          Resume
        </h3>
        {resumeFile && (
          <div
            className="flex items-center gap-3 rounded-xl border p-3.5"
            style={{
              background: "var(--landing-surface)",
              borderColor: "var(--landing-glass-border)",
            }}
          >
            <FileText className="h-4 w-4 shrink-0" style={{ color: "var(--landing-text-muted)" }} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold" style={{ color: "var(--landing-text)" }}>
                {resumeFile.name}
              </p>
              <p className="text-xs" style={{ color: "var(--landing-text-muted)" }}>
                {(resumeFile.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
            {resumeParsed && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold"
                style={{
                  background: "rgba(var(--acc2-rgb),0.08)",
                  color: "#00B0D8",
                  border: "1px solid rgba(var(--acc2-rgb),0.2)",
                }}
              >
                <Check className="h-3 w-3" />
                Parsed
              </span>
            )}
          </div>
        )}
        <TextField
          id="resumeUrl"
          label={resumeFile ? "Resume URL (optional)" : "Resume URL"}
          required={!resumeFile}
          value={resumeUrl}
          onChange={onResumeUrlChange}
          error={resumeUrlError}
          placeholder="https://link-to-your-resume.pdf"
          hint={
            resumeFile
              ? "Your uploaded file will be sent, so a link is optional."
              : "Provide a public link if you didn't upload a file in step 1."
          }
        />
      </section>
    </div>
  );
}
